import { Component, createRef } from 'preact';

import { ChangeType } from '@szn/suggestlist';
import classnames from 'classnames';
import { subscribe } from 'infinite-ui-event';

import DesktopSearch from './component/DesktopSearch';
import MobileSearch from './component/MobileSearch';
import { getSuggestionUrl } from './helpers';
import { hit } from '../../lib/Log';
import { isMobile } from '../../lib/Media';
import ViewContext from '../../view/ViewContext';

const FULLTEXT_SEARCH_QUERY_PARAMETER = 'q';
const MARGIN_ELM_SELECTOR = '[data-ribbon-search-margin]';

export const SEARCH_QUERY_PARAMETER = 'ribbon--search';

export default class Search extends Component {
	static get contextType() {
		return ViewContext;
	}

	constructor() {
		super();

		this._inputRef = createRef();
		this._rootRef = createRef();
		this._mobileSearchRef = createRef();
		this._desktopSearchRef = createRef();

		this._resizeSubscription = null;

		this.state = {
			query: '',
			suggestions: [],
			mobileModalOpened: false
		};

		this.onSubmit = this.onSubmit.bind(this);
		this.onChange = this.onChange.bind(this);
		this.onClear = this.onClear.bind(this);
		this.onClick = this.onClick.bind(this);
		this.onRemoveFromHistory = this.onRemoveFromHistory.bind(this);
		this.onMobileModalToggle = this.onMobileModalToggle.bind(this);
	}

	componentDidMount() {
		this.onResize({
			resize: {
				delta: {
					width: 1
				}
			}
		});

		this._resizeSubscription = subscribe('resize', payload => this.onResize(payload));

		const params = new URLSearchParams(window.location.search);
		const query = decodeURIComponent(
			(
				params.get(SEARCH_QUERY_PARAMETER) ||
				params.get(FULLTEXT_SEARCH_QUERY_PARAMETER) ||
				''
			).replace('+', ' ')
		);

		Promise.all([
			this._loadSuggestions(!query, query),
			new Promise(resolve => {
				if (query) {
					this.setState({ query }, () => resolve());
				} else {
					resolve();
				}
			})
		]).then(() => {
			if (query && !params.has(FULLTEXT_SEARCH_QUERY_PARAMETER)) {
				this._desktopSearchRef.current?.focus();
			}
		});
	}

	componentDidUpdate(prevProps) {
		if (prevProps.searchQuery !== this.props.searchQuery) {
			this.onChange(this.props.searchQuery);
		}
	}

	componentWillUnmount() {
		this._resizeSubscription.unsubscribe();
	}

	render() {
		const { query, suggestions, mobileModalOpened } = this.state;

		return (
			<div
				className={classnames({
					'ribbon-search': true,
					'ribbon-search--hidden': this.props.hidden && !mobileModalOpened
				})}
				ref={this._rootRef}
				role='search'
				aria-labelledby='ribbon-search-0'
			>
				<form
					className='ribbon-search__bar'
					target='_top'
					action='?'
					data-e2e='ribbon-search-form'
					onSubmit={e => {
						e.preventDefault();
						this.onSubmit(query);
					}}
				>
					<h6 className='ribbon--blind' id='ribbon-search-0'>
						Vyhledat
					</h6>
					<DesktopSearch
						ref={this._desktopSearchRef}
						inputRef={this._inputRef}
						searchQueryParameter={SEARCH_QUERY_PARAMETER}
						searchPhrase={query}
						suggestListItems={suggestions}
						onChange={this.onChange}
						onSubmit={this.onSubmit}
						onRemoveFromHistory={this.onRemoveFromHistory}
						onClick={this.onClick}
						onClear={this.onClear}
					/>
				</form>
				<MobileSearch
					ref={this._mobileSearchRef}
					searchQueryParameter={SEARCH_QUERY_PARAMETER}
					searchPhrase={query}
					suggestListItems={suggestions}
					onChange={this.onChange}
					onSubmit={this.onSubmit}
					onMobileModalToggle={this.onMobileModalToggle}
					onRemoveFromHistory={this.onRemoveFromHistory}
				/>
			</div>
		);
	}

	onChange(newSearchPhrase, changeType) {
		if (!newSearchPhrase && !this.state.query) {
			return;
		}

		this.setState({ query: newSearchPhrase }, () => {
			if (changeType !== ChangeType.SELECT) {
				this._loadSuggestions(!newSearchPhrase);
			}
		});
	}

	onClear() {
		this.setState({ query: '', suggestions: [] }, () => {
			this._loadSuggestions(true);

			if (isMobile() && this._mobileSearchRef.current) {
				this.openModalSearch();

				return;
			}

			this._desktopSearchRef.current && this._desktopSearchRef.current.focus();
		});
	}

	onClick(e) {
		if (isMobile() && this._mobileSearchRef.current) {
			e && e.preventDefault();
			this.openModalSearch();
		}
	}

	onMobileModalToggle(isOpen) {
		this.setState({
			mobileModalOpened: isOpen
		});
	}

	async onRemoveFromHistory(item) {
		this.context.suggestApi
			.removeSuggestionFromHistory(item)
			.then(({ response }) => {
				if (response.ok) {
					this._loadSuggestions(true);
				} else {
					throw response;
				}
			})
			.catch(error => console.error('Suggestion remove error:', error));
	}

	onResize(payload) {
		if (payload.resize.delta.width === 0) {
			return;
		}

		const marginElm = document.querySelector(MARGIN_ELM_SELECTOR);
		const { current: rootElm } = this._rootRef;

		if (!rootElm || !marginElm || isMobile()) {
			return;
		}

		rootElm.style.marginLeft = `${Math.max(
			0,
			Math.max(0, marginElm.getBoundingClientRect().left) -
				rootElm.parentElement.getBoundingClientRect().left
		)}px`;
	}

	onSubmit(searchPhrase, selectedSuggestionIndex) {
		if (Number.isInteger(selectedSuggestionIndex) && selectedSuggestionIndex !== -1) {
			// suggested
			const selected = this.state.suggestions[selectedSuggestionIndex] ?? {};

			if (selected?.openUrl && selected?.url) {
				this._openSuggestionUrl(selected.url);
			} else {
				this._openSuggestion(searchPhrase, selected?.ctx, true, selected?.isTopQuery);
			}
		} else {
			// typed
			this._openSuggestion(searchPhrase);
		}

		hit({
			action: 'lista_vyraz_fulltext',
			text: searchPhrase
		});
	}

	openModalSearch() {
		this._mobileSearchRef.current.onChangeModalVisibility(true);
	}

	async _loadSuggestions(trendingTopics, query) {
		const inputRef =
			this._mobileSearchRef?.current?._inputRef ||
			this._desktopSearchRef?.current?._inputRef;
		const suggestions = await this.context.suggestApi.load(
			query || this.state.query,
			inputRef.current?.selection?.start,
			trendingTopics
		);

		return new Promise(resolve => {
			this.setState({ suggestions }, () => resolve());
		});
	}

	_openSuggestion(searchPhrase, ctx = '', isFromSuggest = false, isTopQuery = false) {
		const suggestionUrl = getSuggestionUrl(
			this.context,
			searchPhrase,
			ctx,
			isFromSuggest,
			isTopQuery
		);

		window.location = suggestionUrl;

		// popup wont work in FF
		// if (isDesktop() && !this.context?.props?.search?.openInSameWindow) {
		// 	window.open(suggestionUrl);
		// } else {
		// 	window.location = suggestionUrl;
		// }
	}

	_openSuggestionUrl(url) {
		hit({
			action: 'lista_suggest_clanek',
			text: url
		});

		try {
			const urlObject = new URL(url);
			urlObject.hash = `#utm_medium=suggest&utm_source=${encodeURIComponent(
				this.context?.props.service.id
			)}`;
			window.location = urlObject.toString();
		} catch (_) {
			// if creating URL object fails, redirect to the original URL
			window.location = url;
		}
	}
}
